<template lang="html">
  <form class="team-settings" @submit.prevent="save">
    <div class="section-header">
      <h2>Teams</h2>

      <button
        v-if="isCommissioner && !editing"
        class="small edit"
        type="button"
        @click="editTeams"
      >
        Edit
      </button>

      <div class="actions" v-if="editing">
        <a @click="cancel">Cancel</a>

        <button class="small edit" type="submit">
          Save
        </button>
      </div>
    </div>

    <p>Commissioner can make picks for teams without an owner</p>

    <p class="bold" v-show="editing">Edit draft order, team names, and the owner of each team</p>

    <p class="error" v-show="duplicateNames">Team names must be unique</p>

    <section class="display" v-if="!editing">
      <div class="team" v-for="(team, i) in draft.teams" :key="i">
        <div class="team-position"><b>{{ i +1 }}</b></div>
        <div class="team-name">{{ team.name }}</div>
        <div class="team-email">{{ team.email || 'Empty' }}</div>
      </div>
    </section>

    <section class="editing" v-else>
      <div v-if="teams.length">
        <div class="team" v-for="(team, i) in teams" :key="i">
          <div class="team-position-container">
            <multiselect
              placeholder=""
              v-model="teams[i].position"
              :options="positions"
              @input="changeTeamPosition(i)"
            />
          </div>

          <input
            type="text"
            class="team-name"
            required
            maxlength="20"
            placeholder="Team name"
            v-model="teams[i].name"
          >

          <div class="team-email-container">
            <span v-if="team.uid === commissioner">
              {{ team.email }}
            </span>

            <div class="team-email-edit" v-else>
              <multiselect
                placeholder="Commissioner"
                v-model="teams[i].email"
                :options="owners"
                @input="changeOwner(i)"
              />

              <a v-if="team.uid" @click="removeOwner(i)">Clear</a>
            </div>
          </div>
        </div>

        <button class="small edit" type="submit">
          Save
        </button>
      </div>
    </section>
  </form>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import clonedeep from 'lodash.clonedeep';

export default {
  data() {
    return {
      editing: false,
      teams: [],
      duplicateNames: false,
    };
  },

  computed: {
    ...mapState({
      draft: ({ draft }) => draft.draft,
      commissioner: ({ draft }) => draft.draft.commissioner,
      teamOptions: ({ draft }) => draft.teamOptions,
    }),

    ...mapGetters({
      isCommissioner: 'draft/isCommissioner',
    }),

    owners() {
      const owners = this.draft.teams
        .filter(({ uid }) => {
          return uid && uid !== this.commissioner;
        })
        .map(({ email }) => email)
        .filter((team, i, self) => {
          return self.indexOf(team) === i;
        });

      return owners;
    },

    positions() {
      return this.teams.map((team, i) => {
        return i + 1;
      });
    },
  },

  methods: {
    editTeams() {
      this.teams = clonedeep(this.draft.teams)
        .map((team, i) => {
          team.position = i + 1;
          return team;
        });

      this.editing = true;
    },

    cancel() {
      this.editing = false;
    },

    save() {
      const names = this.teams.map(({ name }) => name);

      const hasDuplicateNames = names.some((name, i) => {
        return names.indexOf(name) !== i;
      });

      if (hasDuplicateNames) {
        this.duplicateNames = true;
      } else {
        this.duplicateNames = false;

        const teams = this.teams.map((team) => {
          const {
            name,
            email,
            uid,
            id,
          } = team;

          const saved = {
            id,
            name,
          };

          if (email && email !== '') {
            saved.email = email;
          }

          if (uid) {
            saved.uid = uid;
          }

          return saved;
        });

        const removedOwners = this.draft.teams
          .filter(({ uid }) => {
            return uid && teams.findIndex(team => team.uid === uid) === -1;
          })
          .map(({ uid }) => uid);

        this.editing = false;

        this.$store.dispatch('draft/SAVE_TEAMS', { teams, removedOwners })
          .then(() => {
            this.$toast({ message: 'Saved your teams' });
          });
      }
    },

    changeTeamPosition(index) {
      this.$nextTick(() => {
        const team2Index = this.teams[index].position - 1;
        const team2 = this.teams[team2Index];

        this.$set(this.teams, team2Index, this.teams[index]);
        this.$set(this.teams, index, team2);
        this.$set(this.teams[index], 'position', index + 1);
      });
    },

    changeOwner(index) {
      const selectedEmail = this.teams[index].email;
      const existingTeam = this.draft.teams.find(({ email }) => selectedEmail === email);

      this.teams[index].uid = existingTeam.uid;
    },

    removeOwner(index) {
      this.$set(this.teams[index], 'email', undefined);
      this.$set(this.teams[index], 'uid', undefined);
    },
  },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import "./settings";

  .team-settings {
    @include settingSection;
    max-width: 100%;
  }

  .team {
    display: flex;
    align-items: center;
    padding: $gp / 2 0;

    &:not(:last-of-type) {
      border-bottom: 1px solid $gray-color;
    }
  }

  .team-position {
    width: 40px;
  }

  .team-email-edit {
    display: flex;
    align-items: center;

    a {
      margin-left: $gp / 2;
    }
  }

  .team-position-container {
    min-width: px-to-rem(240px);
  }

  .team-name,
  .team-email,
  .team-email-container {
    flex: 3;
  }

  .team-email {
    @include ellipsis;
  }

  .team-name,
  .team-email,
  .team-position-container {
    margin-right: $gp;
  }

  .display {
    margin-bottom: $gp;
  }

  form {
    max-width: 100%;
  }

  .team-trash {
    width: px-to-rem(50px);
    margin-left: $gp;
  }

  .editing {
    .team {
      border-bottom: none;
    }

    .team-email-container,
    .team-position-container {
      margin-bottom: $gp;
    }

    @media($small) {
      .team {
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: $gp * 2;
      }
    }
  }
</style>
